/* eslint-disable @typescript-eslint/no-unsafe-call */
import { Button, Grid } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import * as ol from "ol";
import { Feature } from "ol";
import { Coordinate } from "ol/coordinate";
import { FeatureLike } from "ol/Feature";
import GeometryType from "ol/geom/GeometryType";
import Polygon from "ol/geom/Polygon";
import { Draw, Modify, Snap } from "ol/interaction";
import VectorLayer from "ol/layer/Vector";
import { Fill, Stroke, Style, Text } from "ol/style";
import CircleStyle from "ol/style/Circle";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrivalZoneCreateDto,
  ArrivalZoneDetailsDto,
  ArrivalZoneEditDto,
  usePortApi,
} from "~/api/GeneratedApi";
import GenericStepperDialog, {
  DialogStep,
} from "~/components/GenericStepperDialog";
import LoadingBox from "~/components/LoadingBox";
import { OrganizationId } from "~/pages/Management/dialogs/ApiKeyCreationDialog";
import { addArrivalZonesToMap } from "~/pages/MapPage/functions/addArrivalZonesToMap";
import { addPortsToMap } from "~/pages/MapPage/functions/addPortsToMap";
import { findLayerByTitle } from "~/pages/MapPage/functions/findLayerByTitle";
import { mapToReadOnly } from "~/pages/MapPage/functions/mapToReadOnly";
import { useVectorLayer } from "~/pages/MapPage/functions/useVectorLayer";
import useCreateMap from "~/pages/MapPage/hooks/useCreateMap";
import { LayerTitle } from "~/pages/MapPage/types/layers";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import BerthPortSelectForm from "../components/BerthPortSelectForm";
import { useArrivalZones } from "../hooks/useArrivalZones";
import { ArrivalZoneForm, ArrivalZoneFormInputProps } from "./ArrivalZoneForm";

export type DialogAction = "add" | "edit" | "view";

type Props = {
  activeAction?: DialogAction;
  arrivalZoneId?: number;
  open?: boolean;
  portId?: number;
  onClose: () => void;
  onSuccess?: () => void;
};

export type ExtraFormProps = {
  organizationId: OrganizationId;
};

function formDataToArgs(
  x: ArrivalZoneFormInputProps,
  points: Coordinate[],
): (ArrivalZoneCreateDto | ArrivalZoneEditDto) & ExtraFormProps {
  return {
    // ...x,
    description: x?.description ?? "",
    name: x?.name ?? "",
    location: points.map((p) => ({ x: p[0], y: p[1] })) ?? undefined,
    active: x?.active ?? false,
    rolesToNotify: x.rolesToNotify ?? [],
    portId: x.portId ?? -1,
    organizationId: x.organizationId ?? null,
  };
}

const useStyles = makeStyles((theme) => ({
  mapContainer: {
    height: "90%",
    width: "100%",
    marginBottom: theme.spacing(1),
  },
  drawActionButton: {
    display: "block",
    margin: "0 auto",
    color: theme.palette.warning.contrastText,
    backgroundColor: theme.palette.warning.main,
    "&:hover": {
      backgroundColor: theme.palette.warning.dark,
    },
  },
}));
const arrivalZoneStyle = (feature: FeatureLike) =>
  new Style({
    fill: new Fill({
      color: "rgba(255, 255, 255, 0.2)",
    }),
    stroke: new Stroke({
      color: "#004462",
      width: 2,
    }),
    image: new CircleStyle({
      radius: 7,
      fill: new Fill({
        color: "#004462",
      }),
    }),
    text: new Text({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      text: feature.get("name") ?? "",
      // Hidden, for now
      scale: 0,
    }),
  });
export const ArrivalZoneDialog = ({
  activeAction,
  onClose,
  portId,
  arrivalZoneId,
  onSuccess,
  open = true,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("arrival-zones");
  const actions: DialogAction[] = ["add", "edit", "view"];
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<ol.Map>();
  const drawRef = useRef<Draw>();
  const [forceUpdate, setForceUpdate] = React.useState<boolean>(false);
  const { layerSource: drawingLayerSource } = useVectorLayer({
    map: mapRef.current,
    forceUpdate,
    layerOptions: React.useMemo(
      () => ({
        style: arrivalZoneStyle,
      }),
      [],
    ),
  });
  const [drawDone, setDrawDone] = useState(false);
  const classes = useStyles();
  const formId = `${activeAction}-arrival-zone-form`;
  const { createMap } = useCreateMap();
  const nextRef = useRef<() => void>();
  const [selectedPortId, setSelectedPortId] = React.useState<
    number | undefined
  >(portId);
  const [coordinates, setCoordinates] = useState<Coordinate[]>();

  const [formData, setFormData] = React.useState<
    ArrivalZoneDetailsDto | ArrivalZoneEditDto | ArrivalZoneCreateDto
  >();

  const portApi = usePortApi();

  const isEditing = activeAction === "edit";
  const isCreating = activeAction === "add";
  const isViewing = activeAction === "view";

  const {
    arrivalZone,
    arrivalZonesForPort,
    isArrivalZoneLoading,
    addArrivalZoneMutation,
    editArrivalZoneMutation,
  } = useArrivalZones({
    arrivalZoneId,
    selectedPortId,
  });
  React.useEffect(() => {
    if (!arrivalZone) return;
    setFormData(arrivalZone);
    if (arrivalZone.location)
      setCoordinates(arrivalZone.location.map((c) => [c.x, c.y]));
  }, [arrivalZone]);

  const { data: port } = useCustomQuery(
    { key: "port", id: selectedPortId },
    () => portApi.getPort(selectedPortId!),
    { enabled: !!selectedPortId },
  );

  const handleClearDrawing = useCallback(() => {
    if (mapRef.current) {
      drawingLayerSource?.clear();
      setDrawDone(false);
      setCoordinates(undefined);

      if (isEditing || isCreating) {
        drawRef.current = new Draw({
          source: drawingLayerSource,
          type: GeometryType.POLYGON,
        });
        drawRef.current?.on("drawend", () => {
          setDrawDone(true);
          if (drawRef.current) {
            mapRef.current?.removeInteraction(drawRef.current);
          }
        });

        mapRef.current.addInteraction(drawRef.current);
      }
    }
  }, [drawingLayerSource, isCreating, isEditing]);

  const steps: DialogStep[] = useMemo(() => {
    if (isArrivalZoneLoading) {
      return [
        {
          title: t("general:loading"),
          content: <LoadingBox />,
        },
      ];
    }

    let title = "";
    switch (activeAction) {
      case "add":
        title = t("fill-info");
        break;
      case "edit":
        title = t("edit-info");
        break;
      case "view":
        title = arrivalZone?.name ?? "";
        break;
      default:
        break;
    }

    const lastStep = {
      title: isViewing ? title : t("general:confirm"),
      content: (
        <Grid container>
          <Grid
            item
            xs={4}
            ref={mapContainerRef}
            className={classes.mapContainer}
          />

          <Grid item xs={8}>
            <ArrivalZoneForm
              small
              onSubmit={() => null}
              formId={formId}
              zoneDefaultValues={
                formData as ArrivalZoneDetailsDto & ExtraFormProps
              }
              readOnly
            />
          </Grid>
        </Grid>
      ),
    };

    if (isViewing) return [lastStep];

    return [
      {
        title: t("draw-arrival-point"),
        content: (
          <>
            <div ref={mapContainerRef} className={classes.mapContainer} />
            {isCreating && (
              <BerthPortSelectForm
                fullWidth
                portId={selectedPortId}
                onSubmit={(id) => {
                  setSelectedPortId(id);
                }}
              />
            )}
            <Button
              variant="contained"
              classes={{ root: classes.drawActionButton }}
              onClick={handleClearDrawing}
            >
              {t("general:clear")}
            </Button>
          </>
        ),
      },
      {
        title,
        content: (
          <ArrivalZoneForm
            portId={selectedPortId}
            onSubmit={(d) => {
              setFormData(formDataToArgs(d, coordinates!));
              nextRef.current?.();
            }}
            formId={formId}
            zoneDefaultValues={
              formData as ArrivalZoneDetailsDto & ExtraFormProps
            }
            readOnly={isViewing}
          />
        ),
      },
      lastStep,
    ];
  }, [
    activeAction,
    arrivalZone?.name,
    classes.drawActionButton,
    classes.mapContainer,
    coordinates,
    formData,
    formId,
    handleClearDrawing,
    isArrivalZoneLoading,
    isCreating,
    isViewing,
    selectedPortId,
    t,
  ]);

  const handleStepLogic = useCallback(
    (step: number) => {
      setForceUpdate((f) => !f);
      // Add map on step 0 and the required interactions
      if (step !== 0 && step !== 2) {
        mapRef.current?.dispose();
        mapRef.current = undefined;
        drawRef.current = undefined;
      }

      if (step === 0 && mapContainerRef.current) {
        if (!mapRef.current) {
          mapRef.current = createMap({
            target: mapContainerRef.current,
            includedLayers: [
              LayerTitle.OSM,
              LayerTitle.ArrivalZones,
              LayerTitle.Ports,
            ],
            popup: false,
            existingMap: mapRef.current,
          });

          // Set the existing services layer as visible, ignoring the actual settings
          findLayerByTitle(mapRef.current, LayerTitle.ArrivalZones)?.setVisible(
            true,
          );
          // Add the drawing interaction when editing/creating
          if (!isViewing && !coordinates) {
            drawRef.current = new Draw({
              source: drawingLayerSource,
              type: GeometryType.POLYGON,
            });
            mapRef.current.addInteraction(drawRef.current);
          }

          // Add the point of the currently edited service
          if (coordinates) {
            const feature = new Feature<Polygon>({
              geometry: new Polygon([coordinates]),
              name: arrivalZone?.name,
            });

            const ext = feature.getGeometry()?.getExtent();
            if (feature && ext && mapRef.current) {
              drawingLayerSource?.addFeature(feature);

              mapRef.current?.getView().fit(ext);
              setDrawDone(true);
              if (drawRef.current) {
                mapRef.current?.removeInteraction(drawRef.current);
              }
            }
          }

          // Add the modify / snap interactions when editing/creating
          if (!isViewing) {
            const snap = new Snap({ source: drawingLayerSource });
            mapRef.current.addInteraction(snap);
            const modify = new Modify({ source: drawingLayerSource });
            mapRef.current.addInteraction(modify);
            drawRef.current?.on("drawend", () => {
              setDrawDone(true);
              if (drawRef.current) {
                mapRef.current?.removeInteraction(drawRef.current);
              }
            });
          }
        }
        if (mapRef.current) {
          // Add/remove the drawing interaction depending on if we have a port selected or not
          if (isCreating && drawRef.current) {
            if (selectedPortId === undefined) {
              mapRef.current?.removeInteraction(drawRef.current);
            } else if (
              !mapRef.current
                .getInteractions()
                .getArray()
                .includes(drawRef.current)
            ) {
              mapRef.current.addInteraction(drawRef.current);
            }
          }
          if (isCreating)
            addArrivalZonesToMap(mapRef.current, arrivalZonesForPort ?? []);

          // Add the port we are adding this to
          const portLayer = findLayerByTitle(mapRef.current, LayerTitle.Ports);
          portLayer?.setVisible(true);
          addPortsToMap(mapRef.current, [...(port ? [port] : [])]);

          if (port && isCreating) {
            const portFeature = (portLayer as VectorLayer)
              ?.getSource()
              ?.getFeatureById(`port${selectedPortId}`);
            const mapView = mapRef.current?.getView();
            const poly = portFeature.getGeometry() as Polygon;
            const ext = poly.getExtent();
            mapView?.fit(ext);
            const curZoom = mapView?.getZoom();
            if (!curZoom) return;
            mapView?.setZoom(curZoom - 0.2);
          }
        }
      }

      if (step === 2 && mapContainerRef.current && !mapRef.current) {
        mapRef.current = createMap({
          target: mapContainerRef.current,
          includedLayers: [LayerTitle.OSM, LayerTitle.ArrivalZones],
          popup: false,
          existingMap: mapRef.current,
        });

        // Add a layer for the drawing

        // Set the existing layer as visible, ignoring the actual settings
        findLayerByTitle(mapRef.current, LayerTitle.ArrivalZones)?.setVisible(
          true,
        );

        if (coordinates) {
          const polygon = new Polygon([coordinates]);
          const feature = new Feature<Polygon>({
            geometry: polygon,
            name: formData?.name,
          });
          feature.setId("finished");
          drawingLayerSource.addFeature(feature);

          const ext = polygon.getExtent();
          mapRef.current.getView().fit(ext);
          mapRef.current.getView().setZoom(10);
        }
        mapToReadOnly(mapRef.current);
      }
    },
    [
      createMap,
      isViewing,
      coordinates,
      drawingLayerSource,
      arrivalZone?.name,
      isCreating,
      arrivalZonesForPort,
      port,
      selectedPortId,
      formData?.name,
    ],
  );

  const onDrawDone = useCallback(() => {
    const c = (drawingLayerSource?.getFeatures()[0] as Feature<Polygon>)
      ?.getGeometry()
      ?.getCoordinates()[0];
    if (!c) return;
    setCoordinates(c);
  }, [drawingLayerSource]);

  const renderNextButton = useCallback(
    (step, next) => {
      if (isViewing) return null;

      switch (step) {
        case 0:
          return (
            <Button
              variant="contained"
              onClick={(e) => {
                onDrawDone();
                next();
                e.preventDefault();
              }}
              color="secondary"
              disabled={!drawDone}
            >
              {t("general:next")}
            </Button>
          );

        case 1:
          return (
            <Button
              type="submit"
              form={formId}
              variant="contained"
              color="secondary"
            >
              {t("general:next")}
            </Button>
          );
        case 2:
          return (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                if (isCreating) {
                  addArrivalZoneMutation(formData as ArrivalZoneCreateDto, {
                    onSuccess: () => {
                      onSuccess?.();
                      onClose();
                    },
                  });
                } else if (isEditing) {
                  editArrivalZoneMutation(formData as ArrivalZoneEditDto, {
                    onSuccess: () => {
                      onSuccess?.();
                      onClose();
                    },
                  });
                }
              }}
            >
              {t("general:save")}
            </Button>
          );
        default:
          return null;
      }
    },
    [
      isViewing,
      drawDone,
      t,
      formId,
      onDrawDone,
      isCreating,
      isEditing,
      addArrivalZoneMutation,
      formData,
      onSuccess,
      onClose,
      editArrivalZoneMutation,
    ],
  );

  const renderBackButton = useCallback(
    (step, back, handleDialogClose) => {
      return (
        <Button
          onClick={() => {
            if (step === 0) {
              handleDialogClose();
              onClose();
            } else {
              back();
            }
          }}
        >
          {isViewing
            ? t("general:close")
            : t(`general:${step === 0 ? "cancel" : "back"}`)}
        </Button>
      );
    },
    [isViewing, onClose, t],
  );

  if (!activeAction || !actions.includes(activeAction)) return null;

  return (
    <GenericStepperDialog
      open={open}
      steps={steps}
      onClose={onClose}
      dialogTitle=""
      minHeight="40%"
      height="95%"
      contentHeight="80%"
      onStepActive={handleStepLogic}
      renderNextButton={renderNextButton}
      renderBackButton={renderBackButton}
      nextRef={nextRef}
      hideSteps={isViewing || isArrivalZoneLoading}
    />
  );
};
